import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import logoUrl from '../../assets/logos/zoniq.png';
import { eventsApi } from '../../api';
import { isUpcomingDate } from '../../utils/eventDates';
import { categoryMeta, tally } from '../../utils/eventCategories';
import { useIsMobile } from '../../utils/useMediaQuery';
import { tabBarVisibleFor } from './MobileTabBar';
import { Icon } from '../icons/SpriteIcons';
import './shell.css';

export const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const isMobile = useIsMobile();
  const { data } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getAllEvents(),
  });

  const topCategories = useMemo(
    () =>
      tally(
        (data ?? []).filter((e) => isUpcomingDate(e.date)).map((e) => e.category)
      )
        .slice(0, 4)
        .map(([slug]) => ({ slug, label: categoryMeta(slug).label })),
    [data]
  );

  // Focused mobile flows (event detail, checkout, auth, payment) are app-style
  // screens that own the bottom of the viewport with their own sticky action bar,
  // so the marketing footer is suppressed there. These are exactly the routes
  // where the bottom tab bar is also hidden. (After all hooks run.)
  if (isMobile && !tabBarVisibleFor(pathname)) return null;

  // Mobile gets a compact, app-style footer: the 5-column desktop grid reads as a
  // dense wall on a phone, so we collapse to brand + socials and two slim link
  // rows. Primary navigation lives in the bottom tab bar, so this is just the
  // "about / legal" tail of each scroll.
  if (isMobile) {
    return (
      <footer className="zq-shell">
        <div className="border-t border-white/10 bg-gradient-to-b from-transparent to-primary/[0.03] px-5 pb-8 pt-9">
          <Link to="/" className="logo inline-flex" aria-label="Zoniq home">
            <img src={logoUrl} alt="Zoniq" className="h-11 w-auto" />
          </Link>
          <p className="mt-3 max-w-[34ch] text-sm leading-relaxed text-white/50">
            Discover what's happening next — and book it in a few taps.
          </p>
          <div className="mt-4 flex gap-2.5">
            <a className="icon-btn" href="https://www.instagram.com/zoniq.in/" target="_blank" rel="noopener noreferrer" aria-label="Zoniq on Instagram">
              <Icon name="i-ig" size={18} />
            </a>
            <a className="icon-btn" href="https://in.linkedin.com/company/zoniq-in" target="_blank" rel="noopener noreferrer" aria-label="Zoniq on LinkedIn">
              <Icon name="i-li" size={18} />
            </a>
          </div>

          <div className="mt-7 grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
            <Link to="/events" className="text-white/60 transition-colors hover:text-white">All events</Link>
            <a href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer" className="text-white/60 transition-colors hover:text-white">Partner with us</a>
            <Link to="/about" className="text-white/60 transition-colors hover:text-white">About</Link>
            <Link to="/contact" className="text-white/60 transition-colors hover:text-white">Contact</Link>
            <Link to="/terms" className="text-white/60 transition-colors hover:text-white">Terms</Link>
            <Link to="/privacy" className="text-white/60 transition-colors hover:text-white">Privacy</Link>
          </div>

          <div className="mt-7 border-t border-white/10 pt-5 text-xs leading-relaxed text-white/35">
            <p>© {new Date().getFullYear()} Aethel Productions Pvt. Ltd.</p>
            <p className="mt-1">info@zoniq.in</p>
          </div>
        </div>
      </footer>
    );
  }

  return (
    <footer className="zq-shell">
      <div className="ftr">
        <div className="wrap">
          <div className="ftr-grid">
            <div className="brand">
              <Link to="/" className="logo" aria-label="Zoniq home">
                <img src={logoUrl} alt="Zoniq" />
              </Link>
              <p>Discover what's happening next - and book it in a few taps.</p>
              <div className="socials">
                <a
                  className="icon-btn"
                  href="https://www.instagram.com/zoniq.in/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Zoniq on Instagram"
                >
                  <Icon name="i-ig" size={18} />
                </a>
                <a
                  className="icon-btn"
                  href="https://in.linkedin.com/company/zoniq-in"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Zoniq on LinkedIn"
                >
                  <Icon name="i-li" size={18} />
                </a>
              </div>
            </div>

            <div>
              <h4>Discover</h4>
              <ul>
                <li>
                  <Link to="/events">All events</Link>
                </li>
                {topCategories.map((c) => (
                  <li key={c.slug}>
                    <Link to={`/events?category=${encodeURIComponent(c.slug)}`}>{c.label}</Link>
                  </li>
                ))}
                <li>
                  <Link to={{ pathname: '/', hash: '#home-cities' }}>Cities</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4>For partners</h4>
              <ul>
                <li>
                  <a href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer">Partner with us</a>
                </li>
                <li>
                  <Link to="/partners" state={{ ptype: "listing" }}>List an event</Link>
                </li>
                <li>
                  <Link to="/partners" state={{ ptype: "other" }}>Business enquiries</Link>
                </li>
                <li>
                  <a href="https://event-os.zoniq.in/" target="_blank" rel="noopener noreferrer">
                    Zoniq EventOS
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4>Company</h4>
              <ul>
                <li>
                  <Link to="/about">About</Link>
                </li>
                <li>
                  <Link to="/contact">Contact</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4>Support</h4>
              <ul>
                <li>
                  <Link to="/about">FAQs</Link>
                </li>
                <li>
                  <Link to="/terms">Terms</Link>
                </li>
                <li>
                  <Link to="/privacy">Privacy policy</Link>
                </li>
                <li>
                  <Link to="/terms">Refunds &amp; cancellations</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="ftr-base">
            <span>© {new Date().getFullYear()} Aethel Productions Private Limited. All rights reserved.</span>
            <span>info@zoniq.in</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
